import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Shell = ({ children }) => (
  <div className="App min-h-screen bg-slate-50 grid-backdrop flex items-center justify-center p-6">
    <div className="w-full max-w-sm bg-white border border-slate-200 rounded-md p-8 animate-fade-up">
      <div className="flex items-center gap-3 mb-6">
        <img src="/kms-logo.png" alt="KMS logo" className="w-9 h-9 rounded-full object-contain" />
        <div className="font-semibold tracking-tight">KMS-Obligo</div>
      </div>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      <h2 className="text-2xl font-semibold tracking-tight mb-1">Forgot password</h2>
      {sent ? (
        <p data-testid="forgot-sent-message" className="text-sm text-slate-600 mt-3">
          If an account exists for <span className="mono">{email}</span>, we've sent a reset link. It works once and expires in 1 hour.
          Check your spam folder if it doesn't arrive in a few minutes.
        </p>
      ) : (
        <form onSubmit={submit}>
          <p className="text-sm text-slate-500 mb-6">Enter your email and we'll send you a link to choose a new password.</p>
          <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
          <Input data-testid="forgot-email" id="fp-email" type="email" required value={email}
                 onChange={(e) => setEmail(e.target.value)} className="mt-1 mono" />
          <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full mt-6 bg-blue-700 hover:bg-blue-800 text-white">
            {loading ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
      <p className="text-sm text-slate-500 mt-6 text-center">
        <Link to="/login" data-testid="forgot-back-login" className="text-zinc-950 underline underline-offset-4">Back to sign in</Link>
      </p>
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login", { replace: true });
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      <h2 className="text-2xl font-semibold tracking-tight mb-1">Choose a new password</h2>
      {!token ? (
        <p data-testid="reset-missing-token" className="text-sm text-rose-700 mt-3">This reset link is incomplete. Please request a new one.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-5">
          <div>
            <Label htmlFor="rp-pass" className="text-xs uppercase tracking-wider text-slate-500">New password</Label>
            <Input data-testid="reset-password" id="rp-pass" type="password" required value={password}
                   onChange={(e) => setPassword(e.target.value)} className="mt-1" />
            <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
          </div>
          <div>
            <Label htmlFor="rp-confirm" className="text-xs uppercase tracking-wider text-slate-500">Confirm password</Label>
            <Input data-testid="reset-confirm" id="rp-confirm" type="password" required value={confirm}
                   onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white">
            {loading ? "Saving…" : "Update password"}
          </Button>
        </form>
      )}
      <p className="text-sm text-slate-500 mt-6 text-center">
        <Link to="/forgot-password" className="text-zinc-950 underline underline-offset-4">Request a new link</Link>
      </p>
    </Shell>
  );
}
